import {
  ImportCounts,
  ImportField,
  ImportProblem,
} from 'app/core/services/contact-import/contact-import.types';
import { importFieldLabel, isRepeatableField } from './import-fields';

/** Counts as the app writes them: US grouping, "1,204". */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * The mapping after column `index` picks `field`. A field only one column may feed moves:
 * the column that had it is left out ("No importar"), so the pick never gets stuck behind
 * another column. Phones, emails and "No importar" stay on every column that has them.
 */
export function assignField(
  mapping: ImportField[],
  index: number,
  field: ImportField
): ImportField[] {
  const moves = field !== 'IGNORE' && !isRepeatableField(field);
  return mapping.map((old, column) =>
    column === index ? field : moves && old === field ? 'IGNORE' : old
  );
}

/** The review footer's sentence, as an i18n key and its numbers. */
export interface ImportFooter {
  key: string;
  params: Record<string, string>;
  /** "Actualizar reemplaza datos que ya existen." follows. */
  warnsReplace: boolean;
  /** Nothing would be written: Importar stays off. */
  empty: boolean;
}

/** "Vas a crear 1,204 contactos (1,203 también como clientes) y actualizar 2." */
export function importFooter(counts: ImportCounts): ImportFooter {
  const params = {
    create: formatCount(counts.toCreate),
    customers: formatCount(counts.toCreateAsCustomers),
    update: formatCount(counts.toUpdate),
  };
  const creates = counts.toCreate > 0;
  const updates = counts.toUpdate > 0;
  // "(0 también como clientes)" is noise: the sentence leaves the customers out then.
  const plain = counts.toCreateAsCustomers > 0 ? '' : '_PLAIN';
  const key =
    creates && updates
      ? `IMPORT_FOOTER_CREATE_UPDATE${plain}`
      : creates
        ? `IMPORT_FOOTER_CREATE${plain}`
        : updates
          ? 'IMPORT_FOOTER_UPDATE'
          : counts.toMakeCustomers > 0
            ? 'IMPORT_FOOTER_CUSTOMERS_ONLY'
            : 'IMPORT_FOOTER_NOTHING';
  return {
    key,
    params: { ...params, makeCustomers: formatCount(counts.toMakeCustomers) },
    warnsReplace: updates,
    empty: !creates && !updates && counts.toMakeCustomers === 0,
  };
}

/** An import notice read back: the file, and the sentence its counts make. */
export interface ImportNoticeDetail {
  file: string;
  labelKey: string;
  params: Record<string, string>;
}

/**
 * The bell's description, "{file} — {created}/{not imported}/{updated}" (older notices
 * lack the updated count), read back; null for anything else. The updated contacts are
 * named only when there are some.
 */
export function importNoticeDetail(
  description: string | null | undefined
): ImportNoticeDetail | null {
  const match = /^(.*) — (\d+)\/(\d+)(?:\/(\d+))?$/.exec(description ?? '');
  if (!match) {
    return null;
  }
  const params: Record<string, string> = {
    created: formatCount(+match[2]),
    failed: formatCount(+match[3]),
  };
  const updated = +(match[4] ?? 0);
  if (updated === 0) {
    return { file: match[1], labelKey: 'NOTIF_IMPORT_DETAIL', params };
  }
  return {
    file: match[1],
    labelKey: 'NOTIF_IMPORT_DETAIL_UPDATED',
    params: { ...params, updated: formatCount(updated) },
  };
}

/** A problem as its chip reads: the field (or the problem, when it has none) and the value. */
export interface ProblemChip {
  /** i18n key. */
  label: string;
  /** The file's value the message quotes, e.g. "Mujer"; null when it quotes none. */
  value: string | null;
}

/** "Género: Mujer" for `"Mujer" no está en la lista` on the gender field. */
export function problemChip(problem: ImportProblem): ProblemChip {
  const quoted = /"([^"]+)"/.exec(problem.message ?? '');
  return {
    label: problem.field ? importFieldLabel(problem.field) : `IMPORT_PROBLEM_${problem.code}`,
    value: quoted ? quoted[1] : null,
  };
}
